import { useEffect } from 'react';

import { TRecursiveNonNullable } from '@api/Helper.types';
import {
    GetProductOffersResponse,
    PostDocumentUploadResponse
} from '@iwoca/lapi-client/edge';
import classNames from 'classnames';

import styles from './DocumentRequirement.module.css';
import { useFileUploadReducer } from './hooks/useFileUploadReducer';
import { useGetDocuments } from './hooks/useGetDocuments';
import UploadButton from './uploadButton/UploadButton';
import UploadedFiles from './uploadedFiles/UploadedFiles';
import { useStateKey } from '../../../iwQueryClient/useStateKey';

type TGetProductOffersResponseData =
    TRecursiveNonNullable<GetProductOffersResponse>;

type TProps = {
    isOptional?: boolean;
    title?: string;
    children: JSX.Element;
    className?: string;
    documentType?: PostDocumentUploadResponse['data']['document_type'];
    status?: TGetProductOffersResponseData['data']['products'][number]['data_requirements'][number]['status'];
};

const DocumentRequirement = ({
    isOptional,
    title,
    children,
    className,
    documentType
}: TProps) => {
    const stateKey = useStateKey();
    const { data: documentsData } = useGetDocuments(stateKey);

    const {
        uploadedFiles,
        setAllFiles,
        setFileLoading,
        setFileSuccess,
        setFileError,
        setFileReject,
        removeFile
    } = useFileUploadReducer();

    const renderButtonText = () => {
        switch (documentType) {
            case 'aged debtor report':
                return 'Upload report';
            case 'business accounts':
            case 'management accounts':
                return 'Upload accounts';
            default:
                return '';
        }
    };

    useEffect(() => {
        if (documentsData) {
            const documentsForThisDocumentType = documentsData.data.filter(
                (document) => {
                    // @ts-ignore
                    return document.attributes?.document_type === documentType;
                }
            );

            setAllFiles(documentsForThisDocumentType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentType, documentsData]);

    return (
        <div
            id={`document-requirement-${documentType}`}
            className={classNames(
                {
                    [styles.optionalDocumentContainer]: isOptional
                },
                className
            )}
        >
            <div className={styles.documentRequirement}>
                <div className={styles.descriptionContainer}>
                    {title && <h3 className={styles.optionalTitle}>{title}</h3>}
                    <div className={styles.description}>{children}</div>
                </div>
                <div className={styles.buttonContainer}>
                    <UploadButton
                        buttonText={renderButtonText()}
                        classNames={styles.uploadButton}
                        documentType={documentType}
                        setFileLoading={setFileLoading}
                        setFileSuccess={setFileSuccess}
                        setFileError={setFileError}
                        setFileReject={setFileReject}
                    />
                    {uploadedFiles && (
                        <UploadedFiles
                            files={uploadedFiles}
                            removeFile={removeFile}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default DocumentRequirement;
